import React, { useState, useEffect } from 'react';
import { 
    FormControl, 
    FormLabel, 
    FormGroup, 
    FormControlLabel, 
    Checkbox, 
    Box, 
    TextField, 
    Button, 
    IconButton,
    Paper,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const CheckBoxComponent = ({ id, properties, value, valueUpdaterCallback, validityUpdaterCallback }) => {
    const { options = {}, constrains = {} } = properties;
    const mode = properties.mode || 'design';
    // Get checkbox options from properties or saved value
    const initialOptions = value?.options || options.options || ['Option 1', 'Option 2', 'Option 3'];
    const initialSelectedValues = value?.selectedValues || options.defaultValues || [];
    const initialMinOptions = value?.minOptions || constrains?.minOptions || 1;
    const initialMaxOptions = value?.maxOptions || constrains?.maxOptions || 20;
    const initialMinSelections = value?.minSelections || constrains?.minSelections || 0;
    const initialMaxSelections = value?.maxSelections || constrains?.maxSelections || 10;
    
    const [checkboxOptions, setCheckboxOptions] = useState(initialOptions);
    const [selectedValues, setSelectedValues] = useState(initialSelectedValues);
    const [newOptionText, setNewOptionText] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [minOptions, setMinOptions] = useState(initialMinOptions);
    const [maxOptions, setMaxOptions] = useState(initialMaxOptions);
    const [minSelections, setMinSelections] = useState(initialMinSelections);
    const [maxSelections, setMaxSelections] = useState(initialMaxSelections);
    const [showConstraints, setShowConstraints] = useState(false);

    // Sync with parent value changes (when parent updates the value)
    useEffect(() => {
        if (value && typeof value === 'object') {
            if (value.options && Array.isArray(value.options)) {
                setCheckboxOptions(value.options);
            }
            if (value.selectedValues !== undefined) {
                setSelectedValues(value.selectedValues);
            }
            if (value.minOptions !== undefined) {
                setMinOptions(value.minOptions);
            }
            if (value.maxOptions !== undefined) {
                setMaxOptions(value.maxOptions);
            }
            if (value.minSelections !== undefined) {
                setMinSelections(value.minSelections);
            }
            if (value.maxSelections !== undefined) {
                setMaxSelections(value.maxSelections);
            }
        }
    }, [value]);

    // Sync checkbox options with parent when they change
    useEffect(() => {
        if (valueUpdaterCallback) {
            const checkboxData = {
                options: checkboxOptions,
                selectedValues: selectedValues,
                minOptions,
                maxOptions,
                minSelections,
                maxSelections,
            };
            valueUpdaterCallback(checkboxData);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkboxOptions, selectedValues, minOptions, maxOptions, minSelections, maxSelections]);

    const handleCheckboxChange = (optionValue) => {
        let newSelectedValues;
        
        if (selectedValues.includes(optionValue)) {
            // Uncheck - check if min selections constraint is met
            if (selectedValues.length <= minSelections) {
                alert(`You must select at least ${minSelections} option(s)`);
                return;
            }
            newSelectedValues = selectedValues.filter(v => v !== optionValue);
        } else {
            // Check - check if max selections constraint is met
            if (selectedValues.length >= maxSelections) {
                alert(`You can select at most ${maxSelections} option(s)`);
                return;
            }
            newSelectedValues = [...selectedValues, optionValue];
        }
        
        setSelectedValues(newSelectedValues);

        // Validity check - if required, at least minSelections must be selected
        const isValid = options.required 
            ? newSelectedValues.length >= minSelections 
            : true;
        
        if (validityUpdaterCallback) {
            validityUpdaterCallback(isValid);
        }

        // Call custom onChange if provided
        if (options.onChange) {
            options.onChange(newSelectedValues);
        }
    };

    const handleAddOption = () => {
        if (newOptionText.trim() === '') {
            alert('Option text cannot be empty');
            return;
        }

        // Check max options constraint
        if (checkboxOptions.length >= maxOptions) {
            alert(`Maximum ${maxOptions} options allowed`);
            return;
        }

        // Check for duplicate options
        if (checkboxOptions.includes(newOptionText.trim())) {
            alert('This option already exists');
            return;
        }

        setCheckboxOptions([...checkboxOptions, newOptionText.trim()]);
        setNewOptionText('');
    };

    const handleDeleteOption = (index) => {
        // Check min options constraint
        if (checkboxOptions.length <= minOptions) {
            alert(`Minimum ${minOptions} option(s) required`);
            return;
        }

        const deletedOption = checkboxOptions[index];
        const newOptions = checkboxOptions.filter((_, i) => i !== index);
        setCheckboxOptions(newOptions);

        // Remove from selected values if it was selected
        if (selectedValues.includes(deletedOption)) {
            setSelectedValues(selectedValues.filter(v => v !== deletedOption));
        }
    };

    const handleStartEdit = (index) => {
        setEditingIndex(index);
        setEditingText(checkboxOptions[index]);
    };

    const handleSaveEdit = () => {
        if (editingText.trim() === '') {
            alert('Option text cannot be empty');
            return;
        }

        // Check for duplicate options (excluding current one)
        const otherOptions = checkboxOptions.filter((_, i) => i !== editingIndex);
        if (otherOptions.includes(editingText.trim())) {
            alert('This option already exists');
            return;
        }

        const newOptions = [...checkboxOptions];
        const oldValue = newOptions[editingIndex];
        newOptions[editingIndex] = editingText.trim();
        setCheckboxOptions(newOptions);

        // Update selected values if it was selected
        if (selectedValues.includes(oldValue)) {
            setSelectedValues(selectedValues.map(v => v === oldValue ? editingText.trim() : v));
        }

        setEditingIndex(null);
        setEditingText('');
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditingText('');
    };

    const handleMinOptionsChange = (event) => {
        const newMin = parseInt(event.target.value, 10);
        if (isNaN(newMin) || newMin < 1) {
            alert('Minimum options must be at least 1');
            return;
        }
        
        // Ensure min is not greater than max
        if (newMin > maxOptions) {
            alert('Minimum options cannot exceed maximum options');
            return;
        }

        // Ensure current options count meets the new minimum
        if (checkboxOptions.length < newMin) {
            alert(`Current options count (${checkboxOptions.length}) is less than the new minimum (${newMin})`);
            return;
        }

        setMinOptions(newMin);
    };

    const handleMaxOptionsChange = (event) => {
        const newMax = parseInt(event.target.value, 10);
        if (isNaN(newMax) || newMax < 1) {
            alert('Maximum options must be at least 1');
            return;
        }

        // Ensure max is not less than min
        if (newMax < minOptions) {
            alert('Maximum options cannot be less than minimum options');
            return;
        }

        // Ensure current options count doesn't exceed the new maximum
        if (checkboxOptions.length > newMax) {
            alert(`Current options count (${checkboxOptions.length}) exceeds the new maximum (${newMax})`);
            return;
        }

        setMaxOptions(newMax);
    };

    const handleMinSelectionsChange = (event) => {
        const newMin = parseInt(event.target.value, 10);
        if (isNaN(newMin) || newMin < 0) {
            alert('Minimum selections must be at least 0');
            return;
        }
        
        // Ensure min is not greater than max
        if (newMin > maxSelections) {
            alert('Minimum selections cannot exceed maximum selections');
            return;
        }

        setMinSelections(newMin);
    };

    const handleMaxSelectionsChange = (event) => {
        const newMax = parseInt(event.target.value, 10);
        if (isNaN(newMax) || newMax < 0) {
            alert('Maximum selections must be at least 0');
            return;
        }

        // Ensure max is not less than min
        if (newMax < minSelections) {
            alert('Maximum selections cannot be less than minimum selections');
            return;
        }

        // Ensure max doesn't exceed available options
        if (newMax > checkboxOptions.length) {
            alert(`Maximum selections cannot exceed available options (${checkboxOptions.length})`);
            return;
        }

        setMaxSelections(newMax);
    };

    const toggleConstraints = () => {
        setShowConstraints(!showConstraints);
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
            {/* Checkbox Selection Area */}
            <FormControl component="fieldset" required={options.required || false} fullWidth sx={{ width: '100%' }}>
                <FormLabel component="legend">
                    {options.label || 'CheckBox'}
                </FormLabel>
                <FormGroup>
                    {checkboxOptions.map((option, index) => (
                        <Box key={index} display="flex" alignItems="center" width="100%">
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={selectedValues.includes(option)}
                                        onChange={() => handleCheckboxChange(option)}
                                    />
                                }
                                label={
                                    editingIndex === index ? (
                                        <TextField
                                            value={editingText}
                                            onChange={(e) => setEditingText(e.target.value)}
                                            size="small"
                                            autoFocus
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSaveEdit();
                                                }
                                            }}
                                        />
                                    ) : (
                                        option
                                    )
                                }
                                sx={{ flexGrow: 1 }}
                            />
                            {mode === 'design' && (
                                <Box>
                                    {editingIndex === index ? (
                                        <>
                                            <IconButton 
                                                size="small" 
                                                color="primary" 
                                                onClick={handleSaveEdit}
                                            >
                                                <SaveIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton 
                                                size="small" 
                                                onClick={handleCancelEdit}
                                            >
                                                <CancelIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <IconButton 
                                                size="small" 
                                                color="primary" 
                                                onClick={() => handleStartEdit(index)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton 
                                                size="small" 
                                                color="error" 
                                                onClick={() => handleDeleteOption(index)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ))}
                </FormGroup>
            </FormControl>

            {/* Add New Option Area - Only in design mode */}
            {mode === 'design' && (
                <Paper elevation={1} sx={{ p: 2, mt: 2, backgroundColor: '#e8f5e9' }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Add New Option
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                            size="small"
                            placeholder="Enter option text"
                            value={newOptionText}
                            onChange={(e) => setNewOptionText(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddOption();
                                }
                            }}
                            fullWidth
                        />
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<AddIcon />}
                            onClick={handleAddOption}
                            disabled={newOptionText.trim() === ''}
                        >
                            Add
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Constraints Configuration Area - Only in design mode */}
            {mode === 'design' && (
                <Paper elevation={1} sx={{ p: 2, mt: 2, backgroundColor: '#fff9c4' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={showConstraints ? 2 : 0}>
                        <Typography variant="subtitle2">
                            Constraints
                        </Typography>
                        <Button
                            size="small"
                            onClick={toggleConstraints}
                            variant="outlined"
                        >
                            {showConstraints ? 'Hide' : 'Show'}
                        </Button>
                    </Box>
                    
                    {showConstraints && (
                        <Box display="flex" gap={2} flexDirection="column">
                            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                Options Constraints:
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="body2" sx={{ minWidth: '150px' }}>
                                    Min Options:
                                </Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    value={minOptions}
                                    onChange={handleMinOptionsChange}
                                    inputProps={{ min: 1, max: maxOptions }}
                                    sx={{ width: '100px' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    (Current: {checkboxOptions.length})
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="body2" sx={{ minWidth: '150px' }}>
                                    Max Options:
                                </Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    value={maxOptions}
                                    onChange={handleMaxOptionsChange}
                                    inputProps={{ min: minOptions, max: 100 }}
                                    sx={{ width: '100px' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    (Current: {checkboxOptions.length})
                                </Typography>
                            </Box>

                            <Typography variant="caption" color="text.secondary" fontWeight="bold" mt={2}>
                                Selection Constraints:
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="body2" sx={{ minWidth: '150px' }}>
                                    Min Selections:
                                </Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    value={minSelections}
                                    onChange={handleMinSelectionsChange}
                                    inputProps={{ min: 0, max: maxSelections }}
                                    sx={{ width: '100px' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    (Selected: {selectedValues.length})
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="body2" sx={{ minWidth: '150px' }}>
                                    Max Selections:
                                </Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    value={maxSelections}
                                    onChange={handleMaxSelectionsChange}
                                    inputProps={{ min: minSelections, max: checkboxOptions.length }}
                                    sx={{ width: '100px' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    (Selected: {selectedValues.length})
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default CheckBoxComponent;
